import * as React from 'react';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brand, ChatIcon, Asset as AtlasAsset } from '@clearcover/atlas';
import { Asset } from 'Shared/components/Asset';
import { NavigationItem } from '../../types';
import styles from './styles.module.css';
import { RewardsTooltip } from './RewardsTooltip';
import { useHelpdeskControls } from 'Shared/hooks';
import classnames from 'classnames';

type NavigationMenuProps = {
  navItems: NavigationItem[];
} & RouteComponentProps;

const NavigationMenu: React.FC<NavigationMenuProps> = ({ navItems }) => {
  const { loaded: isHelpdeskLoaded, toggleOpen } = useHelpdeskControls();

  const showRewardsTooltipLocal = localStorage.getItem('showRewardsTooltip');
  const [showRewardsTooltip, setShowRewardsTooltip] = React.useState(
    showRewardsTooltipLocal === null
  );

  const navVariants = {
    closed: {
      x: -256,
      opacity: 0,
      transition: { when: 'afterChildren' },
    },
    open: {
      transition: { duration: 0.3, staggerChildren: 0.1 },
      x: 0,
      opacity: 1,
    },
  };

  const itemVariants = {
    open: {
      opacity: 1,
    },
    closed: {
      opacity: 0,
    },
  };

  const rewardsPortalIndex = navItems.findIndex(
    i => i.name === 'Rewards Portal'
  );

  return (
    <motion.div
      variants={navVariants}
      animate="open"
      initial="closed"
      className={styles.container}
    >
      <div className={styles.monogram}>
        <Brand variant="monogram" color="white" />
      </div>
      <nav
        className={styles.mainNav}
        aria-label="Main Navigation"
        role="navigation"
        data-testid="primary-menu"
      >
        <>
          {rewardsPortalIndex !== -1 && (
            <RewardsTooltip
              rewardsPortalIndex={rewardsPortalIndex}
              showTooltip={showRewardsTooltip}
              setShowTooltip={setShowRewardsTooltip}
            />
          )}
          {navItems.map((item, i) => (
            <motion.div
              variants={itemVariants}
              title={item.name}
              key={item.name}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div>
                <NavLink
                  data-testid={item.testId}
                  exact={i === 0}
                  className={classnames(styles.navButton, styles.icon)}
                  activeClassName={styles.active}
                  to={item.path}
                  onClick={() => {
                    if (rewardsPortalIndex === i) {
                      localStorage.setItem('showRewardsTooltip', 'false');
                      setShowRewardsTooltip(false);
                    }
                  }}
                >
                  {item.icon && (
                    <AtlasAsset
                      icon={item.icon}
                      description={item.name}
                      height={24}
                      width={24}
                    />
                  )}
                  {item.assetVariant && <Asset variant={item.assetVariant} />}
                </NavLink>
              </div>
            </motion.div>
          ))}
        </>
      </nav>
      <div className={styles.lowerMenu}>
        {isHelpdeskLoaded && (
          <button
            onClick={toggleOpen}
            title="Chat"
            className={styles.navButton}
          >
            <AtlasAsset
              icon={ChatIcon}
              description={'Contact Helpdesk'}
              className={styles.helpButton}
              height={24}
              width={24}
            />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default withRouter(NavigationMenu);
